import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
import { action, computed } from 'mobx';
import { contextFilterToFormValues } from 'app/util/contextFilters';
import { Model } from 'core/model/Model';
import api from 'core/util/api';
import { timezone, formatDateTime, humanizeDuration } from 'core/util/dateUtils';
import { ALERT_SEVERITY_LABELS, SUPPRESSION_TYPES, SUPPRESSION_TYPE_LABELS, SUPPRESSION_TIMESTAMP_SETTINGS } from '@kentik/ui-shared/alerting/constants';
import safelyParseJSON from '@kentik/ui-shared/util/safelyParseJson';
import { getFilterOperator, CONDITION_OPERATOR_MAP, BOOLEAN_CONDITION_OPERATORS, isEditableKeyValueFilter } from 'app/forms/config/suppressions/suppression';
import $syn from 'app/stores/synthetics/$syn';
import $alerting from 'app/stores/alerting/$alerting';
import { getSuppressionReferenceIds } from '@kentik/ui-shared/alerting/suppressionReferenceIds';
import { getUnavailableReferences, unavailableReferenceLabel, unknownDimensionReferences } from './suppressionReferences';
import { getUnknownDimensions } from './suppressionDimensions';
import { deserializeSuppressionApplications, getSuppressionApplicationLabel } from './suppressionApplications';
import { getSuppressionLifecycleStatus, isManuallyStopped, SUPPRESSION_LIFECYCLE_STATUS, SUPPRESSION_STATUS } from './suppressionLifecycle';
const LEGACY_UNTYPED_SUPPRESSION_TYPES = [SUPPRESSION_TYPES.POLICY, 'partial', 'full'];
let SuppressionModel = (_class = class SuppressionModel extends Model {
  get omitDuringSerialize() {
    return ['uiProperties', 'tableKey', 'user_id'];
  }
  get urlRoot() {
    return '/api/ui/alertingManager/suppressions';
  }
  fetch(options = {}) {
    const label = 'fetching';
    this.requestStatus = label;
    this.requestProgress = 0;
    return api.get(this.url, {
      ...options,
      rawResponse: true
    }).then(success => {
      this.set(this.deserialize(success.body ?? {}));
      this.error = null;
      this.setRequestStatus(null);
      return this.get();
    }, error => {
      this.error = {
        label,
        body: error
      };
      this.requestStatus = null;
      throw error;
    });
  }
  keepsExistingStartTime(startTime) {
    const existingStartTime = this.get('start_time');
    if (this.isNew || !existingStartTime) {
      return false;
    }
    return !startTime || timezone.momentFn(startTime).isSame(existingStartTime);
  }
  serialize(data = {}) {
    const {
      uiProperties,
      start_time,
      end_time
    } = data;
    const ruleIds = data.rule_ids ?? (data.rule_id ? [data.rule_id] : undefined);
    const hasEndTime = Object.prototype.hasOwnProperty.call(data, 'end_time');
    const suppression = super.serialize({
      ...data,
      rule_id: undefined,
      rule_ids: ruleIds ? [...new Set(ruleIds)] : undefined,
      start_time: undefined,
      end_time: hasEndTime ? end_time : undefined
    });
    delete suppression.rule_id;
    const applicationMetadata = safelyParseJSON(suppression.application_metadata || '');
    if (applicationMetadata && typeof applicationMetadata === 'object' && !Array.isArray(applicationMetadata)) {
      const currentApplicationMetadata = Object.fromEntries(Object.entries(applicationMetadata).filter(([key]) => key !== 'application'));
      suppression.application_metadata = JSON.stringify(currentApplicationMetadata, null, 2);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'start_time') || uiProperties?.startAsSoonAsPossible) {
      if (uiProperties?.startAsSoonAsPossible) {
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.AS_SOON_AS_POSSIBLE;
      } else if (this.keepsExistingStartTime(start_time)) {
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.KEEP_UNCHANGED;
      } else {
        suppression.start_time = start_time;
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED;
      }
    }
    if (hasEndTime && !Object.prototype.hasOwnProperty.call(data, 'end_time_code')) {
      suppression.end_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED;
    }
    return suppression;
  }
  async updateOngoing({
    endTime,
    displayName
  }) {
    return this.save({
      display_name: displayName,
      end_time: endTime,
      end_time_code: SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED
    }, {
      sendProvidedValuesOnly: true,
      preserialize: false,
      toast: false
    });
  }
  async stop() {
    return this.save({
      end_time_code: SUPPRESSION_TIMESTAMP_SETTINGS.AS_SOON_AS_POSSIBLE
    }, {
      sendProvidedValuesOnly: true,
      preserialize: false,
      toast: false,
      clearSelection: false
    });
  }
  deserialize(data = {}) {
    const suppression = super.deserialize(data);
    const parsedApplicationMetadata = safelyParseJSON(suppression.application_metadata || '') || {};
    const {
      application: _legacyApplication,
      ...applicationMetadata
    } = parsedApplicationMetadata;
    const {
      rule_id: legacyRuleId,
      ...currentSuppression
    } = suppression;
    const ruleIds = suppression.rule_ids ?? (legacyRuleId ? [legacyRuleId] : undefined);
    const serializedApplicationMetadata = suppression.application_metadata === undefined ? undefined : JSON.stringify(applicationMetadata);
    return {
      ...currentSuppression,
      application_metadata: serializedApplicationMetadata,
      rule_ids: ruleIds,
      uiProperties: {
        startAsSoonAsPossible: !suppression.start_time,
        fetchedAt: Date.now(),
        applicationMetadata
      }
    };
  }
  get sortValues() {
    return {
      expirationDetails: model => model.get('end_time') ?? ''
    };
  }
  get expired() {
    return this.lifecycleStatus === SUPPRESSION_LIFECYCLE_STATUS.EXPIRED;
  }
  get expirationDetails() {
    if (this.isManuallyStopped) {
      return {
        expirationDay: null,
        expired: true,
        duration: null
      };
    }
    const expirationTime = this.get('end_time');
    if (!expirationTime) {
      return {
        expirationDay: null,
        expired: false,
        duration: null
      };
    }
    return {
      expirationDay: formatDateTime(expirationTime),
      duration: humanizeDuration(timezone.momentFn(expirationTime).diff(undefined))
    };
  }
  get formattedStartTime() {
    const startTime = this.get('start_time');
    return formatDateTime(startTime);
  }
  get startDetails() {
    const startTimeMoment = timezone.momentFn(this.get('start_time'));
    return {
      started: startTimeMoment.isSameOrBefore(),
      futureStart: startTimeMoment.isAfter(),
      diff: humanizeDuration(startTimeMoment.diff(undefined))
    };
  }
  get isActive() {
    return this.lifecycleStatus === SUPPRESSION_LIFECYCLE_STATUS.ONGOING;
  }
  get isManuallyStopped() {
    return isManuallyStopped(this.get('end_time_code'));
  }
  get lifecycleStatus() {
    return getSuppressionLifecycleStatus({
      start_time: this.get('start_time'),
      end_time: this.get('end_time'),
      end_time_code: this.get('end_time_code')
    });
  }
  get invalidReferences() {
    const unknownDimensions = getUnknownDimensions(this.rawFilters.map(({
      filterField
    }) => filterField), {
      type: this.type,
      measurement: this.measurement
    });
    return [...getUnavailableReferences({
      ruleIds: this.rule_ids,
      testIds: this.rawTestIds,
      agentIds: this.rawAgentIds
    }, {
      $syn,
      $alerting
    }), ...unknownDimensionReferences(unknownDimensions)];
  }
  get status() {
    return this.lifecycleStatus !== SUPPRESSION_LIFECYCLE_STATUS.EXPIRED && this.invalidReferences.length > 0 ? SUPPRESSION_STATUS.INVALID : this.lifecycleStatus;
  }
  get createdDay() {
    return formatDateTime(this.get('cdate'));
  }
  get comment() {
    return this.get('comment');
  }
  get name() {
    return this.get('display_name')?.trim() || undefined;
  }
  get displayName() {
    return this.name ?? 'Unnamed suppression';
  }
  get hasContextFilter() {
    return Boolean(this.get('context_filter')?.conjunctions?.length);
  }
  get hasPatternRestriction() {
    return (this.get('key_filter')?.filters || []).length > 0;
  }
  get contextFilterEntries() {
    const {
      conjunctions
    } = contextFilterToFormValues(this.get('context_filter'));
    return conjunctions.flatMap(({
      clauses
    }) => clauses.flatMap(({
      predicates
    }) => predicates.flatMap(({
      field,
      value
    }) => (Array.isArray(value) ? value.map(String) : [String(value ?? '')]).map(entry => ({
      field,
      value: entry
    })))));
  }
  get rule_ids() {
    return this.get('rule_ids') || [];
  }
  get messages() {
    return {
      create: `Suppression created${this.name ? ` '${this.name}'` : ''}. It will take effect in a few minutes.`,
      update: `Suppression updated${this.name ? ` '${this.name}'` : ''}`,
      destroy: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} removed successfully`,
      duplicate: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} duplicated successfully`
    };
  }
  get rawAgentIds() {
    return getSuppressionReferenceIds(this.get('key_filter'), ['agentId', 'targetAgentId']);
  }
  get rawTestIds() {
    return getSuppressionReferenceIds(this.get('key_filter'), ['testId']);
  }
  get measurement() {
    return this.get('uiProperties')?.applicationMetadata?.measurement;
  }
  get type() {
    const {
      type
    } = this.get('uiProperties')?.applicationMetadata || {};
    const hasFilters = (this.get('key_filter')?.filters || []).length > 0;

    // raw ids, so a suppression whose tests/agents were deleted doesn't degrade into an unscoped condition
    if (this.rawAgentIds.length > 0) {
      return SUPPRESSION_TYPES.AGENT;
    }
    if (this.rawTestIds.length > 0) {
      return SUPPRESSION_TYPES.SYNTH;
    }
    if (!type || LEGACY_UNTYPED_SUPPRESSION_TYPES.includes(type)) {
      return hasFilters ? SUPPRESSION_TYPES.CONDITION : SUPPRESSION_TYPES.NONE;
    }
    return type;
  }
  get hasAlertType() {
    return this.type !== SUPPRESSION_TYPES.NONE;
  }
  get isSuppression() {
    return true;
  }

  /** Both models answer both questions so the mixed suppression/silence list can discriminate. */
  get isSilence() {
    return false;
  }
  get typeLabel() {
    return SUPPRESSION_TYPE_LABELS[this.type];
  }
  get policyDisplayNames() {
    return this.rule_ids.map(ruleId => {
      const policy = $alerting.policyCollection.getPolicyByRuleId(ruleId);
      return policy?.name || unavailableReferenceLabel('policy');
    });
  }
  get applicationDisplayNames() {
    return (deserializeSuppressionApplications(this.get('applications')) || []).map(getSuppressionApplicationLabel);
  }
  get severityDisplayNames() {
    return (this.get('severities') || []).map(severity => ALERT_SEVERITY_LABELS[severity] || severity);
  }
  get dimensions() {
    const filters = this.get('key_filter')?.filters || [];
    return filters.reduce((allFilters, {
      key,
      value
    }) => {
      const dimensionNames = key?.equals ? [key.equals] : key?.in?.values || [];
      const dimensionValue = value?.equals ?? value?.in?.values;
      dimensionNames.forEach(dimensionName => {
        allFilters[dimensionName] = dimensionValue;
      });
      return allFilters;
    }, {});
  }
  matchesDimensionTarget(target = {}, strict = true) {
    const filters = this.get('key_filter')?.filters || [];
    if (!filters.length) {
      return !strict;
    }
    const covered = new Set();
    const matches = filters.every(filter => {
      const keys = filter.key?.equals ? [filter.key.equals] : filter.key?.in?.values || [];
      const expected = filter.value?.equals !== undefined && filter.value?.equals !== null ? [filter.value.equals] : filter.value?.in?.values || [];
      const hit = keys.find(key => expected.map(String).includes(String(target[key])));
      if (hit) {
        covered.add(hit);
      }
      return Boolean(hit);
    });
    if (!matches) {
      return false;
    }
    if (!strict || filters.some(filter => filter.key?.in?.values?.length)) {
      return true;
    }
    const targetKeys = Object.keys(target).filter(key => target[key] !== undefined && target[key] !== '');
    return targetKeys.length === covered.size && targetKeys.every(key => covered.has(key));
  }
  get rawFilters() {
    const filters = this.get('key_filter')?.filters || [];
    return filters.reduce((acc, filter) => {
      if (!isEditableKeyValueFilter(filter)) {
        return acc;
      }
      const {
        key,
        value
      } = filter;
      const fieldName = key?.equals;
      if (!fieldName) {
        return acc;
      }
      const filterOperator = getFilterOperator(value);
      const operatorKey = CONDITION_OPERATOR_MAP[filterOperator].key;
      const isBooleanOperator = BOOLEAN_CONDITION_OPERATORS.includes(operatorKey);
      let filterValue = value[operatorKey];
      if (isBooleanOperator) {
        filterValue = null;
      } else if (operatorKey === 'in') {
        filterValue = value.in?.values;
      }
      return [...acc, {
        filterField: fieldName,
        filterOperator,
        filterValue
      }];
    }, []);
  }
}, _applyDecoratedDescriptor(_class.prototype, "fetch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetch"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "expired", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "expired"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "expirationDetails", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "expirationDetails"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "formattedStartTime", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "formattedStartTime"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "startDetails", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "startDetails"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isActive", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isActive"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isManuallyStopped", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isManuallyStopped"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "lifecycleStatus", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "lifecycleStatus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "invalidReferences", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "invalidReferences"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "status", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "status"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createdDay", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "createdDay"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasContextFilter", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasContextFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasPatternRestriction", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasPatternRestriction"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "contextFilterEntries", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "contextFilterEntries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rawAgentIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "rawAgentIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rawTestIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "rawTestIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "type", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "type"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasAlertType", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasAlertType"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "policyDisplayNames", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "policyDisplayNames"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dimensions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "dimensions"), _class.prototype), _class);
export { SuppressionModel as default };